import { useState, useEffect, useRef } from 'react';

const steps = [
  {
    number: '01',
    id: 'atc-speaks',
    icon: 'ri-broadcast-line',
    color: '#00A3E0',
    label: 'ATC Speaks',
    headline: 'ATC transmits the clearance',
    description:
      'WingmanFix passively monitors your aviation frequency via Bluetooth headset. The moment ATC begins transmitting, audio capture activates automatically — no button press required.',
    detail: 'Frequency locked · Audio captured · Speaker identified',
    visual: <ATCSpeaksVisual />,
  },
  {
    number: '02',
    id: 'wingman-parses',
    icon: 'ri-cpu-line',
    color: '#00FFD1',
    label: 'WingmanFix Parses',
    headline: 'AI extracts every critical field',
    description:
      'Our aviation-trained language model identifies and structures every element of the clearance in under 2 seconds — destination, route, altitude, departure frequency, and squawk code.',
    detail: 'Parsed in &lt;2s · 97% accuracy · FAA phraseology trained',
    visual: <ParseVisual />,
  },
  {
    number: '03',
    id: 'readback-displayed',
    icon: 'ri-checkbox-circle-line',
    color: '#B4FF39',
    label: 'Readback Displayed',
    headline: 'Full readback phrase ready instantly',
    description:
      'A clean, structured readback is displayed on screen — exactly what you need to say back to ATC. Tap "Read Aloud" and WingmanFix speaks it for you, or read it yourself with confidence.',
    detail: 'Readback phrase · Read Aloud mode · Scored after transmission',
    visual: <ReadbackVisual />,
  },
];

function ATCSpeaksVisual() {
  const [wave, setWave] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setWave((w) => (w + 1) % 60), 80);
    return () => clearInterval(id);
  }, []);

  const bars = Array.from({ length: 28 }, (_, i) => {
    const base = [0.3, 0.5, 0.8, 1, 0.9, 0.6, 0.4, 0.7, 1, 0.8, 0.5, 0.3, 0.6, 0.9, 0.7, 0.4, 0.8, 1, 0.6, 0.3, 0.7, 0.9, 0.5, 0.4, 0.8, 0.6, 0.3, 0.5][i];
    const animated = base + Math.sin((wave + i * 3) * 0.2) * 0.3;
    return Math.max(0.15, Math.min(1, animated));
  });

  return (
    <div className="relative bg-[#050A14] rounded-2xl border border-[#00A3E0]/30 p-4 sm:p-6 overflow-hidden">
      {/* Frequency header */}
      <div className="flex items-center justify-between mb-4 sm:mb-5">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
          <span className="text-[10px] sm:text-xs font-mono text-[#00A3E0] uppercase tracking-widest">LIVE · 124.85 MHz</span>
        </div>
        <span className="text-[10px] sm:text-xs text-gray-500 font-mono">KLNS GND</span>
      </div>

      {/* Waveform */}
      <div className="flex items-center justify-center gap-[2px] sm:gap-[3px] h-12 sm:h-16 mb-4 sm:mb-5">
        {bars.map((h, i) => (
          <div
            key={i}
            className="rounded-full transition-all duration-75"
            style={{
              width: 3,
              height: `${h * 100}%`,
              backgroundColor: `rgba(0, 163, 224, ${0.4 + h * 0.6})`,
            }}
          />
        ))}
      </div>

      {/* ATC speech bubble */}
      <div className="bg-[#0A1628] rounded-xl border border-[#00A3E0]/20 p-3 sm:p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#00A3E0]/20 text-[#00A3E0]">ATC</span>
          <span className="text-[10px] text-gray-600 font-mono">14:07:22</span>
        </div>
        <p className="text-[11px] sm:text-xs text-gray-200 leading-relaxed font-mono">
          "Cessna 4 5 Tango, cleared to Lancaster Airport via direct HAR, then V210. Climb and maintain 3,000. Departure 124.85. Squawk 4721."
        </p>
      </div>

      {/* Capture badge */}
      <div className="mt-3 sm:mt-4 flex items-center gap-2 text-[10px] sm:text-xs text-[#00A3E0]">
        <i className="ri-bluetooth-line"></i>
        <span className="hidden sm:inline">Headset audio captured · Bluetooth active</span>
        <span className="sm:hidden">Audio captured</span>
      </div>
    </div>
  );
}

function ParseVisual() {
  const fields = [
    { label: 'Cleared to', value: 'Lancaster Airport (KLNS)', icon: 'ri-map-pin-line', color: '#00FFD1', delay: 0 },
    { label: 'Route', value: 'Direct HAR VOR → V210', icon: 'ri-route-line', color: '#00A3E0', delay: 150 },
    { label: 'Initial Altitude', value: '3,000 ft', icon: 'ri-arrow-up-line', color: '#B4FF39', delay: 300 },
    { label: 'Departure Freq', value: '124.85 MHz', icon: 'ri-radio-line', color: '#00FFD1', delay: 450 },
    { label: 'Squawk', value: '4721', icon: 'ri-shield-keyhole-line', color: '#00A3E0', delay: 600 },
  ];

  const [visible, setVisible] = useState<boolean[]>(Array(fields.length).fill(false));

  useEffect(() => {
    const timers = fields.map((f, i) =>
      setTimeout(() => setVisible((v) => { const n = [...v]; n[i] = true; return n; }), f.delay + 200)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="bg-[#050A14] rounded-2xl border border-[#00FFD1]/30 p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-4 sm:mb-5">
        <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
          <i className="ri-cpu-line text-[#00FFD1] text-sm sm:text-base animate-spin" style={{ animationDuration: '3s' }}></i>
        </div>
        <span className="text-[10px] sm:text-xs font-mono text-[#00FFD1] uppercase tracking-widest">Parsing clearance…</span>
        <span className="ml-auto text-[10px] sm:text-xs text-gray-600 font-mono">1.4s</span>
      </div>

      <div className="space-y-2 sm:space-y-2.5">
        {fields.map((f, i) => (
          <div
            key={i}
            className={`flex items-center gap-2 sm:gap-3 bg-[#0A1628] rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 border transition-all duration-500 ${
              visible[i] ? 'border-[#00FFD1]/30 opacity-100 translate-x-0' : 'border-transparent opacity-0 -translate-x-4'
            }`}
            style={{ transitionDelay: `${f.delay}ms` }}
          >
            <div className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: `${f.color}15` }}>
              <i className={`${f.icon} text-xs sm:text-sm`} style={{ color: f.color }}></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">{f.label}</p>
              <p className="text-xs sm:text-sm font-semibold text-white truncate">{f.value}</p>
            </div>
            {visible[i] && (
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-[#00FFD1] text-xs"></i>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-3 sm:mt-4 flex items-center justify-between text-[10px] sm:text-xs">
        <span className="text-gray-600 text-[10px] sm:text-xs">Aviation NLP model · FAA trained</span>
        <span className="text-[#00FFD1] font-bold">97% confidence</span>
      </div>
    </div>
  );
}

function ReadbackVisual() {
  const [speaking, setSpeaking] = useState(false);

  return (
    <div className="bg-[#050A14] rounded-2xl border border-[#B4FF39]/30 p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-4 sm:mb-5">
        <div className="w-2 h-2 rounded-full bg-[#B4FF39]"></div>
        <span className="text-[10px] sm:text-xs font-mono text-[#B4FF39] uppercase tracking-widest">Readback Ready</span>
      </div>

      {/* Readback phrase */}
      <div className="bg-[#0A1628] rounded-xl border border-[#B4FF39]/20 p-3 sm:p-4 mb-3 sm:mb-4">
        <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider mb-2">Say this to ATC:</p>
        <p className="text-xs sm:text-sm text-white leading-relaxed font-mono">
          "Cleared to Lancaster Airport, direct HAR, then V210, climb and maintain 3,000, departure 124.85, squawk 4721, Cessna 4 5 Tango."
        </p>
      </div>

      {/* Read Aloud button */}
      <button
        onClick={() => { setSpeaking(true); setTimeout(() => setSpeaking(false), 3000); }}
        className={`w-full flex items-center justify-center gap-2 py-2.5 sm:py-3 rounded-xl font-semibold text-xs sm:text-sm transition-all cursor-pointer whitespace-nowrap ${
          speaking
            ? 'bg-[#B4FF39]/20 border border-[#B4FF39]/60 text-[#B4FF39]'
            : 'bg-[#B4FF39] text-[#0A1628] hover:bg-[#c8ff5a]'
        }`}
      >
        <i className={`${speaking ? 'ri-volume-up-line animate-pulse' : 'ri-volume-up-line'} text-sm sm:text-base`}></i>
        {speaking ? 'Reading aloud…' : 'Read Aloud'}
      </button>

      {/* Score preview */}
      <div className="mt-3 sm:mt-4 grid grid-cols-3 gap-2 text-center">
        {[
          { label: 'Accuracy', value: '97%', color: '#B4FF39' },
          { label: 'Fields', value: '5/5', color: '#00FFD1' },
          { label: 'Time', value: '1.4s', color: '#00A3E0' },
        ].map((s, i) => (
          <div key={i} className="bg-[#0A1628] rounded-lg py-2 px-1 border border-gray-800">
            <p className="text-xs sm:text-sm font-bold" style={{ color: s.color }}>{s.value}</p>
            <p className="text-[10px] text-gray-600 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FeatureIFRHowItWorks() {
  const [activeStep, setActiveStep] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!autoPlay) return;
    intervalRef.current = setInterval(() => {
      setActiveStep((s) => (s + 1) % steps.length);
    }, 4000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoPlay]);

  const handleStepClick = (i: number) => {
    setActiveStep(i);
    setAutoPlay(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const step = steps[activeStep];

  return (
    <section id="ifr-how-it-works" className="py-16 sm:py-24 bg-gradient-to-b from-[#0D1B2E] to-[#0A1628] relative overflow-hidden">
      {/* Background grid */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'linear-gradient(#00A3E0 1px, transparent 1px), linear-gradient(90deg, #00A3E0 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Section header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-[#00A3E0]/10 border border-[#00A3E0]/30 rounded-full px-3 sm:px-4 py-1.5 mb-4 sm:mb-5">
            <i className="ri-route-line text-[#00A3E0] text-xs sm:text-sm"></i>
            <span className="text-[10px] sm:text-xs text-[#00A3E0] uppercase tracking-widest font-semibold">IFR Clearance · How It Works</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-bold mb-4 sm:mb-5 leading-tight px-4">
            From ATC transmission<br />
            <span className="text-[#00A3E0]">to readback</span> in under 2 seconds
          </h2>
          <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed px-4">
            Three seamless steps that eliminate the most stressful moment in IFR flying — copying and reading back a complex clearance under pressure.
          </p>
        </div>

        {/* Step selector tabs - horizontal scroll on mobile */}
        <div className="mb-8 sm:mb-12 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex items-center justify-start sm:justify-center gap-2 sm:gap-4 min-w-max sm:min-w-0">
            {steps.map((s, i) => (
              <button
                key={i}
                onClick={() => handleStepClick(i)}
                className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-3 rounded-full border transition-all duration-300 cursor-pointer whitespace-nowrap flex-shrink-0 ${
                  activeStep === i
                    ? 'border-opacity-60 text-white shadow-lg'
                    : 'border-gray-700 text-gray-500 hover:border-gray-500 hover:text-gray-300'
                }`}
                style={
                  activeStep === i
                    ? { borderColor: s.color, backgroundColor: `${s.color}15`, boxShadow: `0 0 20px ${s.color}20` }
                    : {}
                }
              >
                <span
                  className="text-[10px] sm:text-xs font-bold font-mono"
                  style={{ color: activeStep === i ? s.color : undefined }}
                >
                  {s.number}
                </span>
                <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                  <i className={`${s.icon} text-xs sm:text-sm`} style={{ color: activeStep === i ? s.color : undefined }}></i>
                </div>
                <span className="text-xs sm:text-sm font-semibold">{s.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Progress bar */}
        {autoPlay && (
          <div className="flex gap-2 justify-center mb-8 sm:mb-10">
            {steps.map((s, i) => (
              <div key={i} className="h-0.5 w-16 sm:w-20 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    backgroundColor: s.color,
                    width: i < activeStep ? '100%' : i === activeStep ? '100%' : '0%',
                    transition: i === activeStep ? 'width 4s linear' : 'none',
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Main content panel - stacks on mobile */}
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-10 items-center">
          {/* Left: text */}
          <div key={activeStep} className="animate-fadeIn">
            <div
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-4 sm:mb-6"
              style={{ backgroundColor: `${step.color}15`, color: step.color, border: `1px solid ${step.color}30` }}
            >
              <span className="font-mono">{step.number}</span>
              <span>{step.label}</span>
            </div>

            <h3 className="text-2xl sm:text-4xl font-bold text-white mb-4 sm:mb-5 leading-tight">{step.headline}</h3>
            <p className="text-base sm:text-lg text-gray-400 leading-relaxed mb-6 sm:mb-8">{step.description}</p>

            {/* Detail pill */}
            <div className="flex items-center gap-2 sm:gap-3 bg-[#0D1B2E] border border-gray-700 rounded-xl px-4 sm:px-5 py-2.5 sm:py-3 inline-flex w-fit">
              <i className="ri-information-line text-gray-500 text-xs sm:text-sm"></i>
              <span
                className="text-[10px] sm:text-xs font-mono text-gray-400"
                dangerouslySetInnerHTML={{ __html: step.detail }}
              />
            </div>

            {/* Step navigation arrows */}
            <div className="flex items-center gap-3 mt-8 sm:mt-10">
              <button
                onClick={() => handleStepClick((activeStep - 1 + steps.length) % steps.length)}
                className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full border border-gray-700 text-gray-400 hover:border-gray-500 hover:text-white transition-all cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xs sm:text-sm"></i>
              </button>
              <button
                onClick={() => handleStepClick((activeStep + 1) % steps.length)}
                className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full border border-gray-700 text-gray-400 hover:border-gray-500 hover:text-white transition-all cursor-pointer"
              >
                <i className="ri-arrow-right-line text-xs sm:text-sm"></i>
              </button>
              <button
                onClick={() => setAutoPlay((a) => !a)}
                className="ml-2 flex items-center gap-2 text-[10px] sm:text-xs text-gray-600 hover:text-gray-400 transition-all cursor-pointer"
              >
                <i className={autoPlay ? 'ri-pause-line' : 'ri-play-line'}></i>
                {autoPlay ? 'Pause' : 'Auto-play'}
              </button>
            </div>
          </div>

          {/* Right: visual */}
          <div key={`visual-${activeStep}`} className="animate-fadeIn">
            {step.visual}
          </div>
        </div>

        {/* Bottom connector — flow diagram - stacks on mobile, connector line hidden */}
        <div className="mt-16 sm:mt-20 relative">
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent hidden lg:block" />
          <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 relative z-10">
            {steps.map((s, i) => (
              <button
                key={i}
                onClick={() => handleStepClick(i)}
                className={`group relative bg-[#0D1B2E] rounded-2xl p-4 sm:p-6 border transition-all duration-300 cursor-pointer text-left ${
                  activeStep === i ? 'shadow-xl' : 'border-gray-800 hover:border-gray-600'
                }`}
                style={activeStep === i ? { borderColor: `${s.color}50`, boxShadow: `0 0 30px ${s.color}15` } : {}}
              >
                {/* Connector dot - hidden on mobile */}
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center z-20">
                    <div className="w-2 h-2 rounded-full bg-gray-700"></div>
                  </div>
                )}

                <div className="flex items-start gap-3 sm:gap-4">
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-xl flex-shrink-0 transition-all"
                    style={{
                      backgroundColor: activeStep === i ? `${s.color}20` : '#1A2942',
                      border: `1px solid ${activeStep === i ? `${s.color}40` : 'transparent'}`,
                    }}
                  >
                    <i className={`${s.icon} text-lg sm:text-xl`} style={{ color: activeStep === i ? s.color : '#6B7280' }}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] sm:text-xs font-mono mb-1" style={{ color: activeStep === i ? s.color : '#6B7280' }}>
                      Step {s.number}
                    </p>
                    <p className="text-xs sm:text-sm font-bold text-white mb-1">{s.label}</p>
                    <p className="text-[10px] sm:text-xs text-gray-500 leading-snug">{s.description.slice(0, 72)}…</p>
                  </div>
                </div>

                {activeStep === i && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-b-2xl" style={{ backgroundColor: s.color }} />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease forwards;
        }
      `}</style>
    </section>
  );
}